import type { PreparedSpatialSegment } from "../../components/overlay/types";
import { TIMESTAMP_DISPLAY_DURATION } from "../../constants";
import { toPresentationUs } from "../../utils/time";
import { roiRectToBox } from "../geometry";
import type { FramePrediction, IntervalResult, TrackingInterval } from "../contracts";

/** Find the source frame presented at time. Never interpolate between decoded frame records. */
export function predictionAtTime(frames: readonly FramePrediction[], time: number): FramePrediction | undefined {
  if (!Number.isFinite(time) || time < 0) return undefined;
  // Containers can express fractional microseconds (e.g. 30000/1001 fps),
  // while WebCodecs/rVFC use microsecond presentation clocks. Compare at that
  // shared precision so a rounded callback never selects the previous frame.
  const presentedUs = toPresentationUs(time);
  const index = upperBound(frames, presentedUs, (frame) => toPresentationUs(frame.timestamp));
  const frame = frames[index - 1];
  return frame && presentedUs < toPresentationUs(frame.timestamp + frame.duration) ? frame : undefined;
}

interface TimeRange<T> {
  start: number;
  end: number;
  value: T;
}

interface RangeIndex<T> {
  ranges: TimeRange<T>[];
  /** The largest end at or before each entry, including overlapping VFR endpoints. */
  maximumEnds: number[];
}

function indexRanges<T>(ranges: TimeRange<T>[]): RangeIndex<T> {
  ranges.sort((a, b) => a.start - b.start);
  let maximumEnd = -Infinity;
  return {
    ranges,
    maximumEnds: ranges.map(({ end }) => (maximumEnd = Math.max(maximumEnd, end))),
  };
}

function upperBound<T>(values: readonly T[], time: number, start: (value: T) => number): number {
  let lo = 0;
  let hi = values.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (start(values[mid]!) <= time) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function rangesAtTime<T>({ ranges, maximumEnds }: RangeIndex<T>, time: number): T[] {
  const matches: T[] = [];
  for (let i = upperBound(ranges, time, (range) => range.start) - 1; i >= 0; i--) {
    if (maximumEnds[i]! < time) break;
    if (ranges[i]!.end >= time) matches.push(ranges[i]!.value);
  }
  return matches;
}

interface IndexedInterval {
  interval: TrackingInterval;
  result: IntervalResult;
  order: number;
}

export interface TrackingSelectionIndex {
  byId: ReadonlyMap<string, PreparedSpatialSegment>;
  anchorTracks: ReadonlyMap<string, string>;
  anchorsByTrack: ReadonlyMap<string, readonly PreparedSpatialSegment[]>;
  raw: RangeIndex<{ segment: PreparedSpatialSegment; order: number }>;
  intervalsByTrack: ReadonlyMap<string, RangeIndex<IndexedInterval>>;
}

/** Rebuild for annotation/result changes, never for a playback-clock update. */
export function createTrackingSelectionIndex(
  originals: readonly PreparedSpatialSegment[],
  intervals: readonly TrackingInterval[],
  results: ReadonlyMap<string, IntervalResult>,
): TrackingSelectionIndex {
  const byId = new Map(originals.map((segment) => [segment.id, segment]));
  const anchorTracks = new Map(
    intervals.flatMap((interval) => [
      [interval.start.id, interval.trackId] as const,
      [interval.end.id, interval.trackId] as const,
    ]),
  );
  // Include GT without an eligible interval: it must still replace older predictions.
  for (const segment of originals) {
    if (segment.type === "point_box" && segment.trackId !== undefined) anchorTracks.set(segment.id, segment.trackId);
  }
  const anchorsByTrack = new Map<string, PreparedSpatialSegment[]>();
  for (const segment of originals) {
    const trackId = anchorTracks.get(segment.id);
    if (trackId === undefined) continue;
    const anchors = anchorsByTrack.get(trackId) ?? [];
    anchors.push(segment);
    anchorsByTrack.set(trackId, anchors);
  }
  for (const [trackId, anchors] of anchorsByTrack) {
    anchors.sort((a, b) => a.startTime - b.startTime);
    // As in raw selection, the first GT owns coincident annotation timestamps.
    anchorsByTrack.set(
      trackId,
      anchors.filter((anchor, i) => i === 0 || anchor.startTime !== anchors[i - 1]!.startTime),
    );
  }
  const byTrack = new Map<string, TimeRange<IndexedInterval>[]>();
  intervals.forEach((interval, order) => {
    const result = results.get(interval.id);
    const first = result?.frames[0];
    const last = result?.frames.at(-1);
    if (!result || !first || !last) return;
    const ranges = byTrack.get(interval.trackId) ?? [];
    ranges.push({
      // GT timestamps are inside decoded frames, not necessarily at their starts.
      start: toPresentationUs(first.timestamp),
      end: toPresentationUs(last.timestamp + last.duration),
      value: { interval, result, order },
    });
    byTrack.set(interval.trackId, ranges);
  });
  return {
    byId,
    anchorTracks,
    anchorsByTrack,
    raw: indexRanges(
      originals.map((segment, order) => ({
        start: segment.startTime,
        end:
          segment.startTime === segment.endTime
            ? (segment.displayUntil ?? segment.startTime + TIMESTAMP_DISPLAY_DURATION)
            : segment.endTime,
        value: { segment, order },
      })),
    ),
    intervalsByTrack: new Map([...byTrack].map(([trackId, ranges]) => [trackId, indexRanges(ranges)])),
  };
}

/** Query only raw annotations and decoded intervals covering this presentation time. */
export function selectIndexedTrackedSegments(index: TrackingSelectionIndex, time: number): PreparedSpatialSegment[] {
  const { byId, anchorTracks } = index;
  const raw = rangesAtTime(index.raw, time)
    .filter(
      ({ segment }) =>
        segment.startTime !== segment.endTime ||
        time < (segment.displayUntil ?? segment.startTime + TIMESTAMP_DISPLAY_DURATION),
    )
    .sort((a, b) => a.order - b.order)
    .map(({ segment }) => segment);
  const candidates =
    Number.isFinite(time) && time >= 0
      ? [...index.intervalsByTrack.values()]
          .flatMap((ranges) => rangesAtTime(ranges, toPresentationUs(time)))
          .sort((a, b) => a.order - b.order)
      : [];
  const selected = new Map<string, PreparedSpatialSegment>();
  const selectedStarts = new Map<string, number>();
  const selectedAnchorTimes = new Map<string, number>();
  // Later intervals own shared endpoint frames, guaranteeing one box per track.
  for (const { interval, result } of candidates) {
    const prediction = predictionAtTime(result.frames, time);
    if (!prediction || prediction.method === "unavailable") continue;
    const first = result.frames[0]!;
    const last = result.frames.at(-1)!;
    const anchor = prediction === first ? interval.start : prediction === last ? interval.end : null;
    const source = byId.get(anchor?.id ?? interval.start.id);
    if (!source || source.type !== "point_box") continue;
    if ((selectedStarts.get(interval.trackId) ?? -Infinity) > interval.start.time) continue;
    selectedStarts.set(interval.trackId, interval.start.time);
    selectedAnchorTimes.set(interval.trackId, anchor?.time ?? interval.start.time);
    selected.set(interval.trackId, {
      ...source,
      id: anchor?.id ?? `${interval.trackId}::frame::${prediction.timestamp}`,
      // Copy original GT coordinates exactly, including numeric representation.
      coordinates: anchor?.coordinates ?? roiRectToBox(prediction.box, result.decode),
      trackingMethod: anchor ? "anchor" : prediction.method,
      recovery: anchor ? undefined : prediction.recovery,
      startTime: prediction.timestamp,
      endTime: prediction.timestamp + prediction.duration,
      displayUntil: undefined,
    });
  }
  const visibleRaw = new Set(raw);
  for (const trackId of selected.keys()) {
    const anchors = index.anchorsByTrack.get(trackId) ?? [];
    const latest = anchors[upperBound(anchors, time, (anchor) => anchor.startTime) - 1];
    // A newer GT can share an older decoded endpoint even without a valid interval.
    if (latest && latest.startTime > selectedAnchorTimes.get(trackId)!) {
      if (visibleRaw.has(latest)) selected.set(trackId, latest);
      else selected.delete(trackId);
    }
  }
  return [...raw.filter((segment) => !selected.has(anchorTracks.get(segment.id) ?? "")), ...selected.values()];
}
